import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Typography,
  Collapse,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import LogoutIcon from "@mui/icons-material/Logout";
import AppButton from "../Common/AppButton";
import UserProfileSidebarInfo from "./UserProfileSidebarInfo";
import UserProfileSidebarAvatar from "./UserProfileSidebarAvatar";
import UserProfileSidebarFullName from "./UserProfileSidebarFullName";
import theme from "../../utils/theme";

const styles = {
  drawerContainer: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    bgcolor: theme.palette.secondary.main,
  },
  drawerHeader: (collapsed) => ({
    height: 64,
    bgcolor: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    justifyContent: collapsed ? "center" : "space-between",
    px: collapsed ? 0 : 2,
    transition: "all 0.3s",
  }),
  drawerList: {
    flexGrow: 1,
    pt: 3,
  },
  listItem: (selected, collapsed) => ({
    color: theme.palette.text.light,
    bgcolor: selected ? theme.palette.primary.main : "unset",
    mb: 0.5,
    mx: collapsed ? "auto" : 2,
    borderRadius: 1,
    justifyContent: collapsed ? "center" : "flex-start",
    minHeight: 48,
    width: collapsed ? 48 : "auto",
    transition: "all 0.3s",
    "&:hover": { bgcolor: theme.palette.secondary.light, cursor: "pointer" },
  }),
  listItemIcon: (collapsed) => ({
    color: theme.palette.text.light,
    minWidth: 0,
    mr: collapsed ? 0 : 2,
    justifyContent: "center",
  }),
  subListItem: {
    pl: 4,
  },
  userProfileBox: {
    px: 2,
    py: 2,
    borderTop: `1px solid ${theme.palette.secondary.light}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    bgcolor: theme.palette.secondary.dark,
    minHeight: 120,
  },
};

const Sidebar = ({
  collapsed,
  handleCollapseToggle,
  navItems,
  selectedPage,
  openSubmenus,
  toggleSubmenu,
  navigate,
  handleLogout,
  setCollapsed, // Add setCollapsed to props
}) => {
  const renderNavItem = (item) => {
    const isSelected = selectedPage === item.label;

    if (item.submenu) {
      return (
        <React.Fragment key={item.label}>
          {!collapsed ? (
            <>
              <ListItem
                button
                onClick={() => toggleSubmenu(item.label)}
                sx={styles.listItem(openSubmenus[item.label], collapsed)}
              >
                <ListItemIcon sx={styles.listItemIcon(collapsed)}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
                {openSubmenus[item.label] ? <ExpandLess /> : <ExpandMore />}
              </ListItem>
              <Collapse
                in={openSubmenus[item.label]}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {item.submenuItems.map((subItem) => (
                    <ListItem
                      key={subItem.label}
                      button
                      selected={selectedPage === subItem.label}
                      onClick={() => {
                        navigate(subItem.path);
                      }}
                      sx={{
                        ...styles.listItem(
                          selectedPage === subItem.label,
                          collapsed
                        ),
                        ...styles.subListItem,
                      }}
                    >
                      <ListItemIcon sx={styles.listItemIcon(collapsed)}>
                        {subItem.icon}
                      </ListItemIcon>
                      <ListItemText primary={subItem.label} />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          ) : (
            <ListItem
              button
              onClick={() => toggleSubmenu(item.label)}
              sx={styles.listItem(false, collapsed)}
              title={item.label}
            >
              <ListItemIcon sx={styles.listItemIcon(collapsed)}>
                {item.icon}
              </ListItemIcon>
            </ListItem>
          )}
        </React.Fragment>
      );
    }

    return (
      <ListItem
        key={item.label}
        button
        selected={isSelected}
        onClick={() => {
          if (collapsed) setCollapsed(false);
          navigate(item.path);
          toggleSubmenu();
        }}
        sx={styles.listItem(isSelected, collapsed)}
      >
        <ListItemIcon sx={styles.listItemIcon(collapsed)}>
          {item.icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText primary={item.label} sx={{ opacity: 1 }} />
        )}
      </ListItem>
    );
  };

  return (
    <Box sx={styles.drawerContainer}>
      <Box sx={styles.drawerHeader(collapsed)}>
        <Typography
          variant="h6"
          fontWeight="bold"
          color={theme.palette.text.light}
        >
          {!collapsed ? (
            <Box
              // mb={1}
              ml={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
            >
              <img
                src="/Austin_White.png"
                alt="Austin Logo"
                style={{
                  width: 80,
                  height: 18,
                  textAlign: "center",
                  ml: 0,
                }}
              />
              <Typography
                variant="h3"
                component="span"
                fontWeight={700}
                color={theme.palette.text.light}
                sx={{ letterSpacing: 1, ml: 0.5 }}
              >
                QC
              </Typography>
            </Box>
          ) : (
            <img
              src="/Logo_White.png"
              alt="Austin Logo"
              style={{
                width: 35,
                height: 23,
                textAlign: "center",
              }}
            />
          )}
        </Typography>
        <IconButton
          onClick={handleCollapseToggle}
          size="small"
          sx={{
            color: theme.palette.text.light,
            ml: collapsed ? 0 : 1,
            transition: "all 0.3s",
          }}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>

      <Divider />

      <List sx={styles.drawerList}>
        {navItems.map((item) => renderNavItem(item))}
      </List>

      <Box sx={styles.userProfileBox}>
        <UserProfileSidebarAvatar collapsed={collapsed} />
        {!collapsed && (
          <>
            <UserProfileSidebarFullName />
            <UserProfileSidebarInfo />
          </>
        )}
        <AppButton
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          label={!collapsed && "Log Out"}
          sx={{ mt: 1, pr: 3, pl: 3 }}
        />
      </Box>
    </Box>
  );
};

export default Sidebar;
